import React, { useState, useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import axios from 'axios';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
  updateSpotBalances,
  fetchSpotBalances,
} from '../../store/Slices/assetSlice';
import BASE_URL from '../../API/config';

const LuckWheel = () => {
  const dispatch = useDispatch();
  const theme = useTheme();
  const user = useSelector((state) => state.user.data);
  const isLightTheme = useSelector((state) => state.theme.light);
  const spotBalances = useSelector((state) => state.assets.spotBalances);

  const [spinning, setSpinning] = useState(false);
  const [rotation, setRotation] = useState(0);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const [canSpin, setCanSpin] = useState(true);
  const [confetti, setConfetti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [spinHistory, setSpinHistory] = useState([]);
  const [highlightedSegment, setHighlightedSegment] = useState(null);
  const canvasRef = useRef(null);

  const WHEEL_SIZE = 360;
  const CENTER_COORD = WHEEL_SIZE / 2;
  const RADIUS = WHEEL_SIZE / 2 - 10;
  const SEGMENT_RADIUS = RADIUS - 5; // Inset segments for separation

  const segments = [
    { value: 1, color: 'linear-gradient(45deg, #1E3A8A, #60A5FA)', label: '1%' },
    { value: 3, color: 'linear-gradient(45deg, #6B21A8, #A855F7)', label: '3%' },
    { value: 5, color: 'linear-gradient(45deg, #06B6D4, #22D3EE)', label: '5%' },
    { value: 7, color: 'linear-gradient(45deg, #FBBF24, #FCD34D)', label: '7%' },
    { value: 10, color: 'linear-gradient(45deg, #8B5CF6, #C4B5FD)', label: '10%' },
    { value: 100, color: 'linear-gradient(45deg, #EC4899, #F472B6)', label: '100%' },
  ];

  const segmentAngle = 360 / segments.length;

  const confettiColors = ['#60A5FA', '#A855F7', '#22D3EE', '#FBBF24', '#EC4899'];

  useEffect(() => {
    checkSpinEligibility();
    fetchSpinHistory();
    dispatch(fetchSpotBalances());
    drawWheel();
  }, [dispatch]);

  const drawWheel = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const arc = (2 * Math.PI) / segments.length;

    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw background gradient
    const bgGradient = ctx.createRadialGradient(
      CENTER_COORD, CENTER_COORD, 0,
      CENTER_COORD, CENTER_COORD, RADIUS
    );
    bgGradient.addColorStop(0, isLightTheme ? '#E5E7EB' : '#1E293B');
    bgGradient.addColorStop(1, isLightTheme ? '#D1D5DB' : '#111827');
    ctx.beginPath();
    ctx.arc(CENTER_COORD, CENTER_COORD, RADIUS, 0, 2 * Math.PI);
    ctx.fillStyle = bgGradient;
    ctx.fill();

    // Draw segments with separation
    segments.forEach((segment, i) => {
      const startAngle = i * arc - Math.PI / 2;
      const endAngle = (i + 1) * arc - Math.PI / 2;

      // Draw segment
      ctx.beginPath();
      ctx.moveTo(CENTER_COORD, CENTER_COORD);
      ctx.arc(CENTER_COORD, CENTER_COORD, SEGMENT_RADIUS, startAngle, endAngle);
      ctx.closePath();
      ctx.fillStyle = segment.color;
      ctx.fill();

      // Add 3D effect (inner shadow)
      ctx.beginPath();
      ctx.arc(CENTER_COORD, CENTER_COORD, SEGMENT_RADIUS, startAngle, endAngle);
      ctx.lineWidth = 2;
      ctx.strokeStyle = 'rgba(255, 255, 255, 0.2)';
      ctx.stroke();

      // Highlight winning segment
      if (highlightedSegment === i) {
        ctx.beginPath();
        ctx.arc(CENTER_COORD, CENTER_COORD, SEGMENT_RADIUS, startAngle, endAngle);
        ctx.lineWidth = 4;
        ctx.strokeStyle = '#22D3EE';
        ctx.shadowBlur = 12;
        ctx.shadowColor = 'rgba(34, 211, 238, 0.6)';
        ctx.stroke();
        ctx.shadowBlur = 0;
      }

      // Draw divider
      ctx.beginPath();
      ctx.moveTo(CENTER_COORD, CENTER_COORD);
      const dividerAngle = (i * arc) - Math.PI / 2;
      const x = CENTER_COORD + RADIUS * Math.cos(dividerAngle);
      const y = CENTER_COORD + RADIUS * Math.sin(dividerAngle);
      ctx.lineTo(x, y);
      ctx.lineWidth = 2;
      ctx.strokeStyle = '#22D3EE';
      ctx.shadowBlur = 8;
      ctx.shadowColor = 'rgba(34, 211, 238, 0.4)';
      ctx.stroke();
      ctx.shadowBlur = 0;

      // Draw text
      ctx.save();
      ctx.translate(CENTER_COORD, CENTER_COORD);
      ctx.rotate(startAngle + arc / 2);
      ctx.fillStyle = isLightTheme ? '#1E3A8A' : '#FFFFFF';
      ctx.strokeStyle = isLightTheme ? '#FFFFFF' : '#1E3A8A';
      ctx.lineWidth = 0.5;
      ctx.font = 'bold 18px Orbitron, sans-serif';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(segment.label, SEGMENT_RADIUS - 40, 0);
      ctx.strokeText(segment.label, SEGMENT_RADIUS - 40, 0);
      ctx.restore();
    });

    // Draw pulsating border
    ctx.beginPath();
    ctx.arc(CENTER_COORD, CENTER_COORD, RADIUS, 0, 2 * Math.PI);
    ctx.lineWidth = 12;
    ctx.strokeStyle = '#22D3EE';
    ctx.shadowBlur = 20;
    ctx.shadowColor = 'rgba(34, 211, 238, 0.5)';
    ctx.stroke();
    ctx.shadowBlur = 0;
  };

  const checkSpinEligibility = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('token');
      if (!user?._id) {
        setError('User data not available. Please log in again.');
        setLoading(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/luck-wheel/history/${user._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const today = new Date().setHours(0, 0, 0, 0);
      const hasSpunToday = response.data.some(
        (spin) => new Date(spin.spin_date).setHours(0, 0, 0, 0) === today
      );
      setCanSpin(!hasSpunToday);
      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to check spin eligibility.');
      setLoading(false);
      console.error('Eligibility Check Error:', err);
    }
  };

  const fetchSpinHistory = async () => {
    try {
      setHistoryLoading(true);
      const token = localStorage.getItem('token');
      if (!user?._id) {
        console.warn('User ID not available to fetch spin history.');
        setHistoryLoading(false);
        return;
      }

      const response = await axios.get(
        `${BASE_URL}/luck-wheel/history/${user._id}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      setSpinHistory(response.data);
      setHistoryLoading(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch spin history.');
      setHistoryLoading(false);
      console.error('History Fetch Error:', err);
    }
  };

  const createConfetti = () => {
    const newConfetti = Array.from({ length: 25 }, (_, i) => ({
      id: i,
      left: Math.random() * 100,
      top: Math.random() * -50 - 50,
      delay: Math.random() * 1,
      color: confettiColors[Math.floor(Math.random() * confettiColors.length)],
      size: Math.random() * 5 + 5,
    }));
    setConfetti(newConfetti);
    setTimeout(() => setConfetti([]), 2500);
  };

  const spinWheel = async () => {
    if (!canSpin || spinning) return;
    setSpinning(true);
    setError(null);
    setResult(null);
    setHighlightedSegment(null);

    try {
      if (!user.spot_wallet || typeof user.spot_wallet !== 'string') {
        throw new Error('Spot wallet ID is not available or invalid');
      }

      const token = localStorage.getItem('token');
      const response = await axios.post(
        `${BASE_URL}/luck-wheel/spin`,
        {
          user_id: user._id,
          spot_wallet_id: user.spot_wallet,
        },
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );
      const { reward_percentage, reward_amount } = response.data;
      const segmentIndex = segments.findIndex(
        (s) => s.value === reward_percentage
      );
      if (segmentIndex === -1) {
        throw new Error('Invalid reward percentage returned from server');
      }

      const totalRotations = 5;
      const rotationToLandOnSegment = 360 - (segmentIndex * segmentAngle + segmentAngle / 2);
      const finalRotation = (totalRotations * 360 + rotationToLandOnSegment) % 360;

      setRotation(finalRotation);

      setTimeout(() => {
        setSpinning(false);
        setResult({
          percentage: reward_percentage,
          amount: reward_amount,
        });
        setHighlightedSegment(segmentIndex);
        createConfetti();
        dispatch(fetchSpotBalances());
        fetchSpinHistory();
        setCanSpin(false);
        drawWheel();
      }, 5000);
    } catch (err) {
      setSpinning(false);
      setError(err.response?.data?.message || 'Failed to spin the wheel.');
      console.error('Spin Error:', err);
    }
  };

  const usdtBalance =
    spotBalances.find((b) => b.asset_id?.symbol === 'USDT')?.balance?.toFixed(2) ||
    '0.00';

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: isLightTheme
          ? 'linear-gradient(180deg, #F3F4F6, #D1D5DB)'
          : 'linear-gradient(180deg, #0F172A, #1E293B)',
        color: theme.palette.text.primary,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
        position: 'relative',
        overflow: 'hidden',
        '&:before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: isLightTheme
            ? 'radial-gradient(circle, rgba(96, 165, 250, 0.1) 0%, transparent 70%)'
            : 'radial-gradient(circle, rgba(34, 211, 238, 0.1) 0%, transparent 70%)',
          zIndex: 0,
        },
      }}
    >
      <Box
        sx={{
          maxWidth: 480,
          width: '100%',
          textAlign: 'center',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          zIndex: 1,
        }}
      >
        {/* Balance Card */}
        <Card
          sx={{
            mb: 3,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            background: isLightTheme
              ? 'linear-gradient(145deg, #FFFFFF, #E5E7EB)'
              : 'linear-gradient(145deg, #1E293B, #111827)',
            border: isLightTheme ? '1px solid #E5E7EB' : '1px solid #374151',
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              fontFamily="Orbitron, sans-serif"
              gutterBottom
              color={isLightTheme ? '#1E3A8A' : '#A5B4FC'}
            >
              USDT Wallet Balance
            </Typography>
            <Typography
              variant="h4"
              fontWeight="bold"
              fontFamily="Orbitron, sans-serif"
              color="#22D3EE"
            >
              {usdtBalance} USDT
            </Typography>
          </CardContent>
        </Card>

        {/* Wheel Description */}
        <Box
          sx={{
            maxWidth: '100%',
            mb: 3,
            p: 3,
            bgcolor: isLightTheme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(17, 24, 39, 0.95)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            border: isLightTheme ? '1px solid rgba(0, 0, 0, 0.05)' : '1px solid rgba(255, 255, 255, 0.1)',
          }}
        >
          <Typography
            variant="h6"
            sx={{
              color: isLightTheme ? '#1E3A8A' : '#A5B4FC',
              fontWeight: 'bold',
              fontFamily: 'Orbitron, sans-serif',
              mb: 1,
            }}
          >
            Crypto Luck Wheel
          </Typography>
          <Typography
            variant="body2"
            sx={{
              color: isLightTheme ? '#4B5563' : '#9CA3AF',
              lineHeight: 1.6,
              fontFamily: 'Orbitron, sans-serif',
            }}
          >
            Spin the Crypto Luck Wheel daily to win rewards from 1% to 100% of your USDT balance. Watch the digital wheel spin and claim your crypto prize—added instantly to your wallet. Unleash your luck!
          </Typography>
        </Box>

        {/* Wheel Title */}
        <Typography
          variant="h4"
          fontWeight="bold"
          fontFamily="Orbitron, sans-serif"
          sx={{
            mb: 3,
            background: 'linear-gradient(to right, #22D3EE, #A855F7)',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            textShadow: '0 0 8px rgba(34, 211, 238, 0.3)',
          }}
        >
          Crypto Luck Wheel
        </Typography>

        {/* Wheel Container */}
        <Box sx={{ position: 'relative', width: WHEEL_SIZE, height: WHEEL_SIZE, mb: 4, zIndex: 1 }}>
          <canvas
            ref={canvasRef}
            width={WHEEL_SIZE}
            height={WHEEL_SIZE}
            style={{
              transform: `rotate(${rotation}deg)`,
              transition: spinning ? 'transform 5s cubic-bezier(0.25, 0.1, 0.25, 1)' : 'none',
              borderRadius: '50%',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              top: -15,
              left: '50%',
              transform: 'translateX(-50%) rotate(45deg)',
              width: 20,
              height: 20,
              background: 'linear-gradient(45deg, #22D3EE, #60A5FA)',
              borderRadius: '4px',
              border: '2px solid #FFFFFF',
              zIndex: 2,
              animation: canSpin && !spinning ? 'pulse 1.5s infinite' : spinning ? 'trail 0.2s linear infinite' : 'none',
              boxShadow: '0 0 15px rgba(34, 211, 238, 0.6)',
            }}
          />
        </Box>

        {/* Confetti Container */}
        {confetti.map((item) => (
          <Box
            key={item.id}
            className="confetti"
            sx={{
              position: 'fixed',
              top: `${item.top}vh`,
              left: `${item.left}vw`,
              width: item.size,
              height: item.size,
              background: item.color,
              borderRadius: '30%',
              zIndex: 3,
              animation: `fall ${item.delay + 1}s ease-out forwards`,
              boxShadow: '0 0 10px rgba(255, 255, 255, 0.4)',
              transform: 'rotate(45deg)',
            }}
          />
        ))}

        {/* Spin Button */}
        <Button
          variant="contained"
          onClick={spinWheel}
          disabled={!canSpin || spinning || loading}
          sx={{
            px: 6,
            py: 1.5,
            borderRadius: 2,
            fontWeight: 'bold',
            fontSize: '1.1rem',
            fontFamily: 'Orbitron, sans-serif',
            mb: 2,
            background: 'linear-gradient(45deg, #22D3EE, #A855F7)',
            boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
            '&:hover': {
              background: 'linear-gradient(45deg, #06B6D4, #8B5CF6)',
              boxShadow: '0 6px 24px rgba(0,0,0,0.4)',
              transform: 'scale(1.05)',
            },
            '&:disabled': {
              opacity: 0.5,
              boxShadow: 'none',
              transform: 'none',
            },
          }}
        >
          {spinning ? 'Spinning...' : 'Spin Now'}
        </Button>

        {/* Loading Indicator */}
        {loading && <CircularProgress sx={{ mt: 2, color: '#22D3EE' }} />}

        {/* Result Message */}
        {result && (
          <Typography
            variant="h6"
            fontFamily="Orbitron, sans-serif"
            sx={{
              mt: 2,
              fontWeight: 'bold',
              color: '#FBBF24',
              textShadow: '0 0 10px rgba(251, 191, 36, 0.6)',
              animation: 'resultPulse 1.5s ease-in-out infinite',
            }}
          >
            🎉 You won {result.percentage}% (+{result.amount.toFixed(2)} USDT)
          </Typography>
        )}

        {/* Error Message */}
        {error && (
          <Alert severity="error" sx={{ mt: 2, fontFamily: 'Orbitron, sans-serif' }}>
            {error}
          </Alert>
        )}

        {/* Spin History */}
        <Card
          sx={{
            mt: 3,
            width: '100%',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            background: isLightTheme
              ? 'linear-gradient(145deg, #FFFFFF, #E5E7EB)'
              : 'linear-gradient(145deg, #1E293B, #111827)',
            border: isLightTheme ? '1px solid #E5E7EB' : '1px solid #374151',
          }}
        >
          <CardContent>
            <Typography
              variant="h6"
              fontWeight="bold"
              fontFamily="Orbitron, sans-serif"
              gutterBottom
              color={isLightTheme ? '#1E3A8A' : '#A5B4FC'}
            >
              Spin History
            </Typography>
            {historyLoading ? (
              <CircularProgress sx={{ mt: 2, color: '#22D3EE' }} />
            ) : spinHistory.length === 0 ? (
              <Typography
                variant="body1"
                fontFamily="Orbitron, sans-serif"
                color={isLightTheme ? '#4B5563' : '#9CA3AF'}
              >
                No records yet.
              </Typography>
            ) : (
              <List>
                {spinHistory.map((spin, index) => (
                  <ListItem key={spin._id || index}>
                    <ListItemText
                      primary={`Reward: ${spin.reward_percentage}% (${spin.reward_amount.toFixed(2)} USDT)`}
                      secondary={`Date: ${new Date(spin.spin_date).toLocaleDateString()}`}
                      primaryTypographyProps={{ fontFamily: 'Orbitron, sans-serif', color: isLightTheme ? '#1E3A8A' : '#A5B4FC' }}
                      secondaryTypographyProps={{ fontFamily: 'Orbitron, sans-serif', color: isLightTheme ? '#4B5563' : '#9CA3AF' }}
                    />
                  </ListItem>
                ))}
              </List>
            )}
          </CardContent>
        </Card>
      </Box>
    </Box>
  );
};

export default LuckWheel;